/**
 * PHA-910: shared helpers for the Voice-of-the-Patient (VoP / PCM) goal wizard.
 *
 * Two of the three PHA-910 bugs are pure presentation-layer defects that this module
 * fixes, keeping the logic out of the component so it can be unit-tested:
 *
 *   1. Already-saved goals reappeared in the AI candidate list, because the wizard was
 *      never told what the patient already has. `filterNewGoalSuggestions` removes them.
 *   2. Every goal displayed reference "pco-1", because the LLM hardcodes that value in
 *      `generate_pcm_goal_details.txt` regardless of how many goals exist.
 *      `nextPcoRef` derives the ref the backend will actually assign.
 *
 * NOTE on nextPcoRef: this is a *prediction* for display only. The authoritative ref is
 * minted server-side in `approve_pcm_intake` (app_logic.py) — the wizard deliberately
 * sends an empty pco_ref in ADD mode so the server assigns it. The algorithm here
 * mirrors the server's (`max(existing pco-N) + 1`, else 1) so the preview matches the
 * saved result; if the two ever diverge the server always wins.
 */

export type PcoRefLike = { pco_ref?: string | null };
export type GoalTextLike = { goal_text?: string | null };

/**
 * Normalize goal text for equality comparison: case-insensitive, punctuation-insensitive,
 * whitespace-collapsed. Clinicians edit AI wording lightly (a trailing period, a capital
 * letter), so an exact string match would let near-duplicates slip through.
 */
export const normalizeGoalText = (text: string | null | undefined): string =>
  (text ?? '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, '') // drop punctuation/symbols, keep letters+digits
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Drop AI suggestions the patient has already saved as a goal.
 *
 * This is the client-side safety net. The backend also excludes existing goals from the
 * generation prompt (PHA-910), but that path depends on a deployed prompt + cache-key
 * change, and the LLM is not obliged to comply — so we filter here unconditionally.
 */
export const filterNewGoalSuggestions = <T extends GoalTextLike>(
  suggestions: readonly T[],
  existingGoals: readonly GoalTextLike[] | null | undefined
): T[] => {
  const taken = new Set(
    (existingGoals ?? [])
      .map(g => normalizeGoalText(g.goal_text))
      .filter(Boolean)
  );
  if (taken.size === 0) return [...suggestions];
  return suggestions.filter(s => {
    const key = normalizeGoalText(s.goal_text);
    // Keep blank-text suggestions rather than silently dropping them — an empty
    // normalization is not evidence of a duplicate.
    return key === '' || !taken.has(key);
  });
};

/**
 * Predict the pco_ref the backend will assign to the next NEW goal.
 * Mirrors `approve_pcm_intake`: highest existing `pco-<N>` + 1, starting at 1.
 * Refs that don't match `pco-<N>` (legacy/hand-entered) are ignored, exactly as
 * the server's regex ignores them.
 */
export const nextPcoRef = (existingGoals: readonly PcoRefLike[] | null | undefined): string => {
  const used = (existingGoals ?? [])
    .map(g => /^pco-(\d+)$/.exec((g.pco_ref ?? '').trim())?.[1])
    .filter((n): n is string => n != null)
    .map(Number)
    .filter(n => Number.isFinite(n));
  const next = used.length > 0 ? Math.max(...used) + 1 : 1;
  return `pco-${next}`;
};
